import React, { useContext, useEffect, useState } from "react";
import { Alert, Button, Col, Form, Row, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { SessionContext } from "../SessionProvider";
import reportCardApi, { errorMessage } from "./reportCardApi";
import ClassSectionPicker from "./ClassSectionPicker";
import PageShell from "./PageShell";

const key = (studentId, fieldId) => `${studentId}|${fieldId}`;

/**
 * Route: /reportcard/footer
 * Grid: students x footer fields (manual fields + auto fields that teachers may override).
 */
const FooterEntry = () => {
  const { selectedSessionId } = useContext(SessionContext);
  const [filter, setFilter] = useState({ class_id: "", section_id: "" });
  const [examGroups, setExamGroups] = useState([]);
  const [examGroupId, setExamGroupId] = useState("");
  const [data, setData] = useState({ students: [], fields: [] });
  const [values, setValues] = useState({});
  const [classes, setClasses] = useState([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    reportCardApi.examGroups.list().then((l) => setExamGroups(l.filter((g) => g.status === "active"))).catch(() => {});
    reportCardApi.activeClasses().then(setClasses).catch(() => {});
  }, []);

  const ready = filter.class_id && filter.section_id && selectedSessionId;

  const load = async () => {
    if (!ready) return;
    try {
      const res = await reportCardApi.footerValues.get({ ...filter, sessionid: selectedSessionId, exam_group_id: examGroupId || undefined });
      // editable = manual fields + auto fields with "teacher can change"
      const fields = (res.fields || []).filter((f) => f.value_source === "MANUAL" || f.allow_override);
      setData({ students: res.students || [], fields });
      const v = {};
      (res.values || []).forEach((r) => {
        const f = fields.find((x) => x.id === r.report_footer_field_id);
        if (!f) return;
        // term fields: only the selected term's value; year fields: the NULL-group value
        if (f.scope === "EXAM_GROUP" ? r.exam_group_id !== (examGroupId || null) : r.exam_group_id !== null) return;
        v[key(r.student_addmission_id, r.report_footer_field_id)] = r.value_text ?? "";
      });
      setValues(v);
      setDirty(false);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, [filter.class_id, filter.section_id, examGroupId, selectedSessionId]);

  const hasTermFields = data.fields.some((f) => f.scope === "EXAM_GROUP");
  const visibleFields = data.fields.filter((f) => f.scope === "SESSION" || examGroupId);

  const set = (sid, fid, v) => {
    setValues((x) => ({ ...x, [key(sid, fid)]: v }));
    setDirty(true);
  };

  const fillColumn = (f, v) => {
    setValues((x) => {
      const next = { ...x };
      data.students.forEach((s) => { next[key(s.student_addmission_id, f.id)] = v; });
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    const missing = [];
    visibleFields.filter((f) => f.is_required && f.value_source === "MANUAL").forEach((f) => {
      data.students.forEach((s) => { if (!values[key(s.student_addmission_id, f.id)]) missing.push(`${s.student_name}: ${f.title}`); });
    });
    if (missing.length) {
      toast.error(`Required: ${missing.slice(0, 3).join(", ")}${missing.length > 3 ? ` (+${missing.length - 3} more)` : ""}`);
      return;
    }
    const records = [];
    data.students.forEach((s) => visibleFields.forEach((f) => {
      records.push({ report_footer_field_id: f.id, student_addmission_id: s.student_addmission_id, value_text: values[key(s.student_addmission_id, f.id)] ?? "" });
    }));
    try {
      const res = await reportCardApi.footerValues.save({ sessionid: selectedSessionId, exam_group_id: examGroupId || null, records });
      toast.success(res.message || "Saved");
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const input = (s, f) => {
    const v = values[key(s.student_addmission_id, f.id)] ?? "";
    const on = (e) => set(s.student_addmission_id, f.id, e.target.value);
    const placeholder = f.value_source === "AUTO" ? "auto" : f.default_value || "";
    switch (f.input_type) {
      case "TEXTAREA":
        return <Form.Control as="textarea" rows={2} size="sm" value={v} placeholder={placeholder} onChange={on} />;
      case "NUMBER":
        return <Form.Control type="number" size="sm" value={v} placeholder={placeholder} onChange={on} />;
      case "DATE":
        return <Form.Control type="date" size="sm" value={v} onChange={on} />;
      case "DROPDOWN":
        return (
          <Form.Select size="sm" value={v} onChange={on}>
            <option value="">{placeholder || "—"}</option>
            {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
          </Form.Select>
        );
      case "CLASS":
        return (
          <Form.Select size="sm" value={v} onChange={on}>
            <option value="">{f.value_source === "AUTO" ? "auto" : "—"}</option>
            {classes.map((c) => <option key={c.id} value={c.class_name || c.classname}>{c.class_name || c.classname}</option>)}
          </Form.Select>
        );
      default:
        return <Form.Control size="sm" value={v} placeholder={placeholder || (f.input_type === "FRACTION" ? "e.g. 180 / 200" : "")} onChange={on} />;
    }
  };

  return (
    <PageShell title="Remarks & Footer">
      <Row className="g-2 mb-3 align-items-end">
        <ClassSectionPicker value={filter} onChange={setFilter} md={3} />
        {(hasTermFields || examGroupId) && (
          <Col md={3}>
            <Form.Label className="form-view-label">Term (for per-term fields)</Form.Label>
            <Form.Select value={examGroupId} onChange={(e) => setExamGroupId(e.target.value)}>
              <option value="">-- Year fields only --</option>
              {examGroups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </Form.Select>
          </Col>
        )}
        <Col className="text-end">
          {ready && visibleFields.length > 0 && <Button size="sm" onClick={save} disabled={!dirty}>Save</Button>}
        </Col>
      </Row>

      {!selectedSessionId && <Alert variant="warning">Select a session in the header first.</Alert>}
      {ready && data.fields.length === 0 && (
        <Alert variant="info">No editable footer fields for this class. Add Manual fields (e.g. Class Teacher's Remarks) in Report Card Settings → Report Footer.</Alert>
      )}
      {ready && data.fields.some((f) => f.value_source === "AUTO") && (
        <Alert variant="light" className="py-2 border">
          <small>Fields marked <b>auto</b> are calculated by the system. Leave them blank to use the calculated value; type a value only to override it.</small>
        </Alert>
      )}

      {ready && visibleFields.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <Table bordered size="sm" className="align-middle">
            <thead>
              <tr>
                <th style={{ width: 60 }}>Roll</th>
                <th style={{ minWidth: 180 }}>Student</th>
                {visibleFields.map((f) => (
                  <th key={f.id} style={{ minWidth: f.input_type === "TEXTAREA" ? 260 : 160 }}>
                    {f.title}
                    {f.is_required && <span className="text-danger"> *</span>}
                    {f.value_source === "AUTO" && <small className="text-muted"> (auto)</small>}
                    {f.scope === "EXAM_GROUP" && <small className="text-muted"> (term)</small>}
                    {f.input_type === "DROPDOWN" && (
                      <Form.Select size="sm" className="mt-1" value="" onChange={(e) => e.target.value && fillColumn(f, e.target.value)}>
                        <option value="">Fill all…</option>
                        {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                      </Form.Select>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.students.map((s) => (
                <tr key={s.student_addmission_id}>
                  <td>{s.roll_no || ""}</td>
                  <td>{s.student_name}</td>
                  {visibleFields.map((f) => <td key={f.id}>{input(s, f)}</td>)}
                </tr>
              ))}
              {data.students.length === 0 && (
                <tr><td colSpan={2 + visibleFields.length} className="text-center text-muted">No active students in this section</td></tr>
              )}
            </tbody>
          </Table>
        </div>
      )}
    </PageShell>
  );
};

export default FooterEntry;
